/**
 * Spline primitives for the camera flythrough.
 *
 * Pure math — no Mapbox, no React, no store. Everything here is exercised by
 * `selfCheck.ts`.
 *
 * Why centripetal Catmull-Rom (alpha = 0.5) rather than the uniform variant
 * already in `demo/masters-planner/motion/easing.ts`: uniform Catmull-Rom is
 * only well-behaved when control points are roughly evenly spaced. Camera
 * waypoints are captured by hand and routinely are not — two shots a few metres
 * apart followed by one a kilometre away is normal. Uniform CR forms cusps and
 * self-intersecting loops in exactly that case; centripetal is provably free of
 * both. That guarantee is the whole reason for the extra knot arithmetic.
 */

/** Number of arc-length samples taken per segment at compile time. */
export const ARC_SAMPLES = 24

/** Catmull-Rom parameterization exponent. 0.5 = centripetal. */
const ALPHA = 0.5

/** Knot spacing below which a segment is treated as degenerate. */
const KNOT_EPSILON = 1e-9

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v

/** Wrap an index into [0, n) for a closed (looping) control-point ring. */
export const wrapIndex = (i: number, n: number): number => ((i % n) + n) % n

/** Shortest signed angular delta from `from` to `to`, in degrees, in (-180, 180]. */
export const angleDelta = (from: number, to: number): number =>
  (((to - from) % 360) + 540) % 360 - 180

/** Normalize any angle to [0, 360). */
export const wrap360 = (deg: number): number => ((deg % 360) + 360) % 360

/**
 * Convert a bearing sequence into a continuous real-valued one by accumulating
 * shortest-arc deltas.
 *
 * Splining raw 0..360 values would send the camera the long way around at every
 * wraparound (e.g. 350 -> 10 would spin 340 degrees backwards). We unroll once
 * at compile time, spline the reals, and wrap only when sampling.
 */
export function unrollBearings(bearings: readonly number[]): Float64Array {
  const out = new Float64Array(bearings.length)
  if (bearings.length === 0) return out
  out[0] = bearings[0]
  for (let i = 1; i < bearings.length; i++) {
    out[i] = out[i - 1] + angleDelta(bearings[i - 1], bearings[i])
  }
  return out
}

/**
 * Centripetal Catmull-Rom knot sequence for four control points.
 * Returns [t0, t1, t2, t3]; t0 is always 0.
 */
export function centripetalKnots(
  p0: readonly [number, number],
  p1: readonly [number, number],
  p2: readonly [number, number],
  p3: readonly [number, number]
): [number, number, number, number] {
  const step = (a: readonly [number, number], b: readonly [number, number]): number => {
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]
    return Math.pow(Math.hypot(dx, dy), ALPHA)
  }
  const t0 = 0
  const t1 = t0 + step(p0, p1)
  const t2 = t1 + step(p1, p2)
  const t3 = t2 + step(p2, p3)
  return [t0, t1, t2, t3]
}

/**
 * Evaluate a centripetal Catmull-Rom spline on the span p1 -> p2 at u in [0,1],
 * using the Barry-Goldman pyramid.
 *
 * The pyramid form is used rather than the cubic-basis matrix because it stays
 * numerically stable as knot spacings become uneven — which is the normal case
 * for hand-captured waypoints, and the reason we chose centripetal at all.
 *
 * Falls back to a straight lerp when any knot interval collapses (coincident
 * control points), which would otherwise divide by zero.
 */
export function catmullRom2D(
  p0: readonly [number, number],
  p1: readonly [number, number],
  p2: readonly [number, number],
  p3: readonly [number, number],
  u: number
): [number, number] {
  const [t0, t1, t2, t3] = centripetalKnots(p0, p1, p2, p3)

  if (t1 - t0 < KNOT_EPSILON || t2 - t1 < KNOT_EPSILON || t3 - t2 < KNOT_EPSILON) {
    return [p1[0] + (p2[0] - p1[0]) * u, p1[1] + (p2[1] - p1[1]) * u]
  }

  const t = t1 + (t2 - t1) * u

  const lerp = (
    a: readonly [number, number],
    b: readonly [number, number],
    ta: number,
    tb: number
  ): [number, number] => {
    const w = (tb - t) / (tb - ta)
    const v = (t - ta) / (tb - ta)
    return [a[0] * w + b[0] * v, a[1] * w + b[1] * v]
  }

  const a1 = lerp(p0, p1, t0, t1)
  const a2 = lerp(p1, p2, t1, t2)
  const a3 = lerp(p2, p3, t2, t3)
  const b1 = lerp(a1, a2, t0, t2)
  const b2 = lerp(a2, a3, t1, t3)
  return lerp(b1, b2, t1, t2)
}

/**
 * Uniform Catmull-Rom for a scalar channel (zoom, pitch, bearing).
 *
 * Scalars have no meaningful "distance" to parameterize by, so the uniform form
 * is correct here. Callers MUST clamp the result — Catmull-Rom overshoots, and
 * an out-of-range pitch or zoom makes Mapbox throw.
 */
export function catmullRom1D(a: number, b: number, c: number, d: number, u: number): number {
  const u2 = u * u
  const u3 = u2 * u
  return 0.5 * (2 * b + (-a + c) * u + (2 * a - 5 * b + 4 * c - d) * u2 + (-a + 3 * b - 3 * c + d) * u3)
}

/**
 * Trapezoidal velocity warp: ramp up over the first `R` of the run, hold at
 * constant speed, ramp down over the last `R`.
 *
 * This is the whole easing story. Applying an ease per segment would zero the
 * velocity at every waypoint; applying one ramp per *run* eases the start and
 * end of a continuous move while leaving the interior gliding.
 *
 * Derivation: velocity v(s) = smoothstep(s/R) on the ramps, 1 in the middle.
 * `warp` is its integral normalized so warp(1) = 1. Since the integral of
 * smoothstep over [0,1] is 1/2, the total unnormalized area is (1 - R), which
 * is the divisor `D`.
 *
 * Continuous and C1 by construction: both branches give R/2 at s = R.
 *
 * @param s Normalized progress through the run, 0..1.
 * @param R Ramp fraction, 0..0.49. R = 0 disables the ramp entirely.
 */
export function warp(s: number, R: number): number {
  if (R <= 0) return s
  const D = 1 - R
  if (s < R) {
    const x = s / R
    return (R * (x * x * x - (x * x * x * x) / 2)) / D
  }
  if (s > 1 - R) {
    const y = (1 - s) / R
    return (D - R * (y * y * y - (y * y * y * y) / 2)) / D
  }
  return (R / 2 + (s - R)) / D
}

/**
 * Read a uniformly-sampled 0..1 lookup table with linear interpolation.
 * `at` is the position along the table's own axis, in [0,1].
 */
export function sampleTable(table: Float64Array, at: number): number {
  const n = table.length - 1
  if (n < 1) return at
  const x = clamp(at, 0, 1) * n
  const i = Math.min(n - 1, Math.floor(x))
  const local = x - i
  return table[i] + (table[i + 1] - table[i]) * local
}

/**
 * Invert a cumulative arc-length table: given a target fraction of total length,
 * return the spline parameter u in [0,1] that reaches it.
 *
 * This is what makes `durationMs` mean "seconds to fly this leg" at constant
 * geometric speed — without it, the camera would slow down and speed up
 * according to the spline's own parameterization.
 */
export function arcLengthToU(arc: Float64Array, fraction: number): number {
  const n = arc.length - 1
  const total = arc[n]
  if (!(total > 0)) return fraction

  const target = clamp(fraction, 0, 1) * total

  // Binary search for the last index whose cumulative length is <= target.
  let lo = 0
  let hi = n
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (arc[mid] <= target) lo = mid
    else hi = mid - 1
  }
  if (lo >= n) return 1

  const span = arc[lo + 1] - arc[lo]
  const local = span > 0 ? (target - arc[lo]) / span : 0
  return (lo + local) / n
}
